import { apiClient } from '../api-client';
import { ApiResponse } from '../../dtos/common/api.dto';
import {
  Medication,
  MedicationStatus,
  MedicationsResponse,
  MedicationResponse,
  MedicationGroups,
  MedicationHistoryEntry,
  MedicationHistoryResponse,
  OrderingPhysician,
} from '../../dtos/patientmr/medication.dto';

// Re-export types for backward compatibility
export type { Medication, MedicationsResponse, MedicationResponse, MedicationGroups, MedicationHistoryEntry };

/**
 * Recursively remove keys with empty string, null, undefined, empty array, or empty object values
 */
function cleanPayload<T>(obj: T): T {
  if (Array.isArray(obj)) {
    // Clean each item and filter out empty items
    return obj
      .map(cleanPayload)
      .filter(
        (item) =>
          item !== undefined &&
          item !== null &&
          !(typeof item === 'string' && item.trim() === '') &&
          !(Array.isArray(item) && item.length === 0) &&
          !(typeof item === 'object' && item && Object.keys(item).length === 0)
      ) as unknown as T;
  } else if (typeof obj === 'object' && obj !== null) {
    const cleaned: any = {};
    Object.entries(obj).forEach(([key, value]) => {
      const cleanedValue = cleanPayload(value);
      if (
        cleanedValue !== undefined &&
        cleanedValue !== null &&
        !(typeof cleanedValue === 'string' && cleanedValue.trim() === '') &&
        !(Array.isArray(cleanedValue) && cleanedValue.length === 0) &&
        !(typeof cleanedValue === 'object' && cleanedValue && Object.keys(cleanedValue).length === 0)
      ) {
        cleaned[key] = cleanedValue;
      }
    });
    return cleaned;
  }
  return obj;
}

// Form data type remains in service (UI-specific, not API contract)
export interface MedicationFormData {
  name: string;
  dose: string;
  /** Route code from constants/medRoutes (MED-01) */
  route: string;
  /** Free text when route is OTHER */
  routeOther?: string;
  frequency: string;
  instructions: string;
  expirationDate: string;
  status: MedicationStatus;
  prn?: boolean;
  /** Required when prn is true */
  prnIndication?: string;
  startDate?: string;
  orderingPhysician?: OrderingPhysician;
}

/** Client-side check of the medication form. Returns field → message (empty when valid). */
export function validateMedicationForm(data: MedicationFormData): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.name.trim()) errors.name = 'Medication name is required.';
  if (!data.dose.trim()) errors.dose = 'Dose is required.';
  if (!data.route) errors.route = 'Route is required.';
  if (data.route === 'OTHER' && !(data.routeOther || '').trim()) errors.routeOther = 'Describe the route.';
  if (!data.frequency.trim()) errors.frequency = 'Frequency is required.';
  if (!data.instructions.trim()) errors.instructions = 'Instructions are required.';
  if (data.prn && !(data.prnIndication || '').trim()) errors.prnIndication = 'A PRN medication needs an indication (when to give it).';
  const npi = data.orderingPhysician?.npi?.trim();
  if (npi && !/^\d{10}$/.test(npi)) errors.orderingPhysicianNpi = 'NPI must be 10 digits.';
  return errors;
}

/** Split a flat list into Active / On hold / Inactive (used when the server sends only `medications`). */
export function groupMedications(response: MedicationsResponse): MedicationGroups {
  if (response.active || response.onHold || response.inactive) {
    return {
      active: response.active ?? [],
      onHold: response.onHold ?? [],
      inactive: response.inactive ?? [],
    };
  }
  const all = response.medications ?? [];
  return {
    active: all.filter((m) => m.status === 'ACTIVE'),
    onHold: all.filter((m) => m.status === 'ON_HOLD'),
    inactive: all.filter((m) => m.status === 'DISCONTINUED'),
  };
}

/**
 * Fetch all medications for a patient
 */
export const fetchMedications = async (patientId: string): Promise<Medication[]> => {
  try {
    const response: ApiResponse<MedicationsResponse> = await apiClient.get(`/patientmr/${patientId}/medications`);

    if (response.success && response.data) {
      return response.data.medications || [];
    }

    throw new Error(response.error?.message || 'Failed to fetch medications');
  } catch (error) {
    console.error('Error fetching medications:', error);
    throw error;
  }
};

/**
 * Fetch medications grouped into Active / On hold / Inactive (MED-02)
 */
export const fetchMedicationGroups = async (patientId: string): Promise<MedicationGroups> => {
  const response: ApiResponse<MedicationsResponse> = await apiClient.get(`/patientmr/${patientId}/medications`);
  if (response.success && response.data) {
    return groupMedications(response.data);
  }
  throw new Error(response.error?.message || 'Failed to fetch medications');
};

/**
 * Change history of one medication (MED-02), newest first
 */
export const fetchMedicationHistory = async (patientId: string, medicationId: string): Promise<MedicationHistoryEntry[]> => {
  const response: ApiResponse<MedicationHistoryResponse> = await apiClient.get(
    `/patientmr/${patientId}/medications/${medicationId}/history`
  );
  if (response.success && response.data) {
    return [...(response.data.history || [])].sort((a, b) => (a.changedAt < b.changedAt ? 1 : -1));
  }
  throw new Error(response.error?.message || 'Failed to fetch medication history');
};

/**
 * Add a new medication for a patient
 */
export const addMedication = async (patientId: string, medicationData: MedicationFormData): Promise<Medication> => {
  try {
    // Clean the payload to remove empty fields
    const payload = cleanPayload({
      ...medicationData
    });

    const response: ApiResponse<MedicationResponse> = await apiClient.post(`/patientmr/${patientId}/medications`, payload);

    if (response.success && response.data) {
      return response.data.medication;
    }

    throw new Error(response.error?.message || 'Failed to add medication');
  } catch (error) {
    console.error('Error adding medication:', error);
    throw error;
  }
};

/**
 * Update an existing medication for a patient.
 * Accepts either a full MedicationFormData or a partial update payload.
 */
export const updateMedication = async (
  patientId: string,
  medicationId: string,
  medicationData: MedicationFormData | Partial<MedicationFormData & { discontinuationReason: string; onHoldReason: string }>
): Promise<Medication> => {
  try {
    // Clean the payload to remove empty fields
    const payload = cleanPayload({
      ...medicationData
    });

    const response: ApiResponse<MedicationResponse> = await apiClient.put(`/patientmr/${patientId}/medications/${medicationId}`, payload);

    if (response.success && response.data) {
      return response.data.medication;
    }

    throw new Error(response.error?.message || 'Failed to update medication');
  } catch (error) {
    console.error('Error updating medication:', error);
    throw error;
  }
};

/**
 * Delete a medication for a patient
 */
export const deleteMedication = async (patientId: string, medicationId: string): Promise<boolean> => {
  try {
    const response: ApiResponse<void> = await apiClient.delete(`/patientmr/${patientId}/medications/${medicationId}`);

    if (response.success) {
      return true;
    }

    throw new Error(response.error?.message || 'Failed to delete medication');
  } catch (error) {
    console.error('Error deleting medication:', error);
    throw error;
  }
};
